import { motion } from "framer-motion";
import { CloudSun, MoonStar, NotebookPen, Sunrise } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const BENEFITS = [
  {
    id: "benefit-card-moments",
    stat: "5",
    unit: "minutes a day",
    title: "A calmer mind",
    description:
      "A short daily pause to write down what went right gently shifts your attention away from worry and toward the good that's already here.",
    icon: Sunrise,
    span: "lg:col-span-7",
    bg: "bg-[#FAF1EE] border-terracotta/20",
    accent: "text-terracotta",
  },
  {
    id: "benefit-card-sleep",
    stat: "3",
    unit: "good things",
    title: "A softer night",
    description:
      "Naming three moments you're thankful for before bed helps the day end on warmth instead of rumination.",
    icon: MoonStar,
    span: "lg:col-span-5",
    bg: "bg-[#F2F5F1] border-sage/25",
    accent: "text-sagedark",
  },
  {
    id: "benefit-card-habit",
    stat: "120",
    unit: "guided pages",
    title: "A lasting ritual",
    description:
      "What gets written gets remembered. Page by page, noticing blessings becomes second nature — a habit that quietly rewires your outlook.",
    icon: NotebookPen,
    span: "lg:col-span-5",
    bg: "bg-paper border-linenborder",
    accent: "text-sienna",
  },
  {
    id: "benefit-card-presence",
    stat: "1",
    unit: "life, fully loved",
    title: "A present heart",
    description:
      "Stop rushing toward the next milestone. Gratitude teaches you to savor the life you are actively living — right now, exactly as it is.",
    icon: CloudSun,
    span: "lg:col-span-7",
    bg: "bg-paper border-linenborder",
    accent: "text-gold",
  },
];

export const Benefits = () => (
  <section
    id="benefits"
    data-testid="benefits-section"
    className="relative bg-cream/60 py-24 sm:py-32 lg:py-36"
  >
    <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className="mb-16 max-w-2xl sm:mb-20"
      >
        <p className="mb-4 text-xs font-semibold uppercase tracking-[0.28em] text-terracotta">
          Why Gratitude
        </p>
        <h2 className="font-serif text-3xl font-medium leading-[1.1] tracking-tight text-espresso sm:text-5xl">
          The science &amp; soul
          <br />
          <em className="italic text-sienna">of saying thank you.</em>
        </h2>
        <p className="mt-5 text-base leading-relaxed text-walnut sm:text-lg">
          Researchers keep finding the same thing: people who practice written
          gratitude feel happier, sleep deeper, and stress less. All it takes
          is a pen and a few honest lines.
        </p>
      </motion.div>

      <div
        data-testid="benefits-grid-container"
        className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-12"
      >
        {BENEFITS.map((b, i) => (
          <motion.div
            key={b.id}
            data-testid={b.id}
            initial={{ opacity: 0, y: 44 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.85, delay: i * 0.1, ease: EASE }}
            whileHover={{ y: -6 }}
            className={`rounded-3xl border p-8 shadow-[0_2px_12px_rgba(34,26,22,0.04)] transition-shadow duration-500 hover:shadow-[0_24px_50px_-20px_rgba(139,58,36,0.22)] sm:p-10 md:col-span-1 ${b.span} ${b.bg}`}
          >
            <div className="flex items-start justify-between">
              <div>
                <span
                  className={`font-serif text-6xl font-light leading-none sm:text-7xl ${b.accent}`}
                >
                  {b.stat}
                </span>
                <span className="mt-1 block text-xs font-semibold uppercase tracking-[0.2em] text-sand">
                  {b.unit}
                </span>
              </div>
              <span className="rounded-full border border-linenborder/80 bg-paper/80 p-3">
                <b.icon size={20} className={b.accent} strokeWidth={1.6} />
              </span>
            </div>
            <h3 className="mt-8 font-serif text-2xl font-medium text-espresso sm:text-3xl">
              {b.title}
            </h3>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-walnut/90 sm:text-base">
              {b.description}
            </p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
